import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAdminCreators,
  updateCreatorRole,
  updateCreatorStatus,
  updateCreatorTrust,
  AdminCreatorSortField,
  SortDirection,
  AdminCreatorsPaginatedResponse
} from '../services/api';
import Navbar from '../components/Navbar';
import Button from '../components/Button';
import { t } from '../i18n';
import './AdminCreators.css';

const ADMIN_CREATORS_STATE_KEY = 'admin_creators_list_state_v1';

const getStoredAdminCreatorsState = () => {
  try {
    const raw = sessionStorage.getItem(ADMIN_CREATORS_STATE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

interface Creator {
  id: string;
  alias: string;
  email: string;
  role: 'creator' | 'manager' | 'administrator';
  status: 'pending' | 'approved' | 'rejected';
  createdAt?: string;
  creation_date?: string;
  avatar_url?: string;
  emailVerified?: number | boolean;
  hide?: number;
  isTrusted?: number | boolean;
  rating?: number;
  views?: number;
}

type SortField = AdminCreatorSortField;

const AdminCreatorsPage: React.FC = () => {
  const navigate = useNavigate();
  const storedState = getStoredAdminCreatorsState();
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(Math.max(Number(storedState?.page) || 1, 1));
  const [pageSize, setPageSize] = useState(() => {
    const candidate = Number(storedState?.pageSize);
    return [25, 50, 100].includes(candidate) ? candidate : 25;
  });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState<{ field: SortField; direction: SortDirection }>({
    field: (storedState?.sortField as SortField) || 'createdAt',
    direction: storedState?.sortDirection === 'asc' ? 'asc' : 'desc'
  });

  const loadCreators = async () => {
    try {
      setLoading(true);
      const response = await getAdminCreators({
        page,
        limit: pageSize,
        sortBy: sortConfig.field,
        sortDir: sortConfig.direction
      });

      const payload = response.data;

      if (Array.isArray(payload)) {
        setCreators(payload as unknown as Creator[]);
        setTotalItems(payload.length);
        setTotalPages(1);
      } else {
        const paginated = payload as AdminCreatorsPaginatedResponse;
        setCreators((paginated.items || []) as Creator[]);
        setTotalItems(paginated.pagination?.totalItems || 0);
        setTotalPages(paginated.pagination?.totalPages || 1);
      }
    } catch (err: any) {
      console.error(err);
      setError(t('adminCreators.error.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setError('');
    loadCreators();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortConfig.field, sortConfig.direction]);

  useEffect(() => {
    sessionStorage.setItem(
      ADMIN_CREATORS_STATE_KEY,
      JSON.stringify({
        page,
        pageSize,
        sortField: sortConfig.field,
        sortDirection: sortConfig.direction
      })
    );
  }, [page, pageSize, sortConfig.field, sortConfig.direction]);

  const handleRoleChange = async (id: string, newRole: string) => {
    try {
      await updateCreatorRole(id, newRole);
      setCreators(prev => prev.map(c => c.id === id ? { ...c, role: newRole as any } : c));
      setSuccess(t('adminCreators.success.roleUpdated'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(t('adminCreators.error.roleUpdate'));
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await updateCreatorStatus(id, newStatus);
      setCreators(prev => prev.map(c => c.id === id ? { ...c, status: newStatus as any } : c));
      setSuccess(t('adminCreators.success.statusUpdated'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(t('adminCreators.error.statusUpdate'));
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleTrustChange = async (id: string, nextTrusted: boolean) => {
    try {
      await updateCreatorTrust(id, nextTrusted);
      setCreators(prev => prev.map(c => c.id === id ? { ...c, isTrusted: nextTrusted ? 1 : 0 } : c));
      setSuccess(t('adminCreators.success.trustUpdated'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(t('adminCreators.error.trustUpdate'));
      setTimeout(() => setError(''), 5000);
    }
  };

  const requestSort = (field: SortField) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setPage(1);
  };

  const getSortArrow = (field: SortField) => {
    if (sortConfig.field !== field) {
      return '↕';
    }

    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div className="page-container">
        <Navbar />
        <main className="main-content">
          <div className="loading-spinner">{t('adminCreators.loading')}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Navbar />
      
      <main className="main-content" style={{ maxWidth: '1500px', margin: '0 auto', width: '100%' }}>
        <div style={{ marginBottom: '1rem' }}>
          <Button variant="ghost" onClick={() => navigate('/')}>
            {t('adminXperiences.backDashboard')}
          </Button>
        </div>

        <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1>{t('adminCreators.title')}</h1>
        </div>

        {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}
        {success && <div className="success-message" style={{ marginBottom: '1rem', padding: '1rem', background: 'rgba(46, 213, 115, 0.1)', border: '1px solid rgba(46, 213, 115, 0.2)', color: '#2ed573', borderRadius: '8px' }}>{success}</div>}

        <div className="creators-table-container" style={{ background: '#1e1e24', borderRadius: '12px', padding: '0.75rem', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                  <button type="button" onClick={() => requestSort('alias')} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px', padding: 0 }}>
                    {t('adminCreators.table.alias')} <span>{getSortArrow('alias')}</span>
                  </button>
                </th>
                <th style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                  <button type="button" onClick={() => requestSort('email')} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px', padding: 0 }}>
                    {t('adminCreators.table.email')} <span>{getSortArrow('email')}</span>
                  </button>
                </th>
                <th style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                  <button type="button" onClick={() => requestSort('emailVerified')} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px', padding: 0 }}>
                    {t('adminCreators.table.emailVerified')} <span>{getSortArrow('emailVerified')}</span>
                  </button>
                </th>
                <th style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                  <button type="button" onClick={() => requestSort('hide')} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px', padding: 0 }}>
                    {t('adminCreators.table.hidden')} <span>{getSortArrow('hide')}</span>
                  </button>
                </th>
                <th style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                  <button type="button" onClick={() => requestSort('rating')} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px', padding: 0 }}>
                    {t('profile.stats.rating')} <span>{getSortArrow('rating')}</span>
                  </button>
                </th>
                <th style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                  <button type="button" onClick={() => requestSort('views')} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px', padding: 0 }}>
                    {t('profile.stats.views')} <span>{getSortArrow('views')}</span>
                  </button>
                </th>
                <th style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                  <button type="button" onClick={() => requestSort('createdAt')} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px', padding: 0 }}>
                    {t('adminCreators.table.joinDate')} <span>{getSortArrow('createdAt')}</span>
                  </button>
                </th>
                <th style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                  <button type="button" onClick={() => requestSort('role')} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px', padding: 0 }}>
                    {t('adminCreators.table.role')} <span>{getSortArrow('role')}</span>
                  </button>
                </th>
                <th style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                  <button type="button" onClick={() => requestSort('status')} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px', padding: 0 }}>
                    {t('adminCreators.table.status')} <span>{getSortArrow('status')}</span>
                  </button>
                </th>
                <th style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                  <button type="button" onClick={() => requestSort('isTrusted')} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px', padding: 0 }}>
                    {t('adminCreators.table.trusted')} <span>{getSortArrow('isTrusted')}</span>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {creators.map(creator => (
                <tr key={creator.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }} onClick={() => navigate(`/admin/creators/${creator.id}`)}>
                  <td style={{ padding: '0.75rem', fontWeight: 500 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      {creator.avatar_url ? (
                        <img src={`https://x-perienc.es/${creator.avatar_url}`} alt={creator.alias} style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: '14px' }}>{creator.alias.charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                      {creator.alias}
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>{creator.email}</td>
                  <td style={{ padding: '0.75rem' }}>
                    <span style={{ 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      fontSize: '0.8rem',
                      background: creator.emailVerified ? 'rgba(46, 213, 115, 0.2)' : 'rgba(255, 71, 87, 0.2)',
                      color: creator.emailVerified ? '#2ed573' : '#ff4757'
                    }}>
                      {creator.emailVerified ? t('adminCreators.yes') : t('adminCreators.no')}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    <span style={{ 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      fontSize: '0.8rem',
                      background: creator.hide ? 'rgba(255, 165, 2, 0.2)' : 'rgba(46, 213, 115, 0.2)',
                      color: creator.hide ? '#ffa502' : '#2ed573'
                    }}>
                      {creator.hide ? t('adminCreators.hidden') : t('adminCreators.visible')}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.8)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    ⭐ {creator.rating ? Number(creator.rating).toFixed(1) : '0.0'}
                  </td>
                  <td style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.8)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    👁️ {creator.views || 0}
                  </td>
                  <td style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    {new Date(creator.createdAt || creator.creation_date || '').toLocaleDateString()}
                  </td>
                  <td style={{ padding: '0.75rem' }} onClick={(e) => e.stopPropagation()}>
                    <select 
                      value={creator.role} 
                      onChange={(e) => handleRoleChange(creator.id, e.target.value)}
                      style={{ 
                        background: 'rgba(0,0,0,0.3)', 
                        border: '1px solid rgba(255,255,255,0.1)', 
                        color: 'white', 
                        padding: '0.5rem', 
                        borderRadius: '6px',
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="creator">{t('adminCreators.role.creator')}</option>
                      <option value="manager">{t('adminCreators.role.manager')}</option>
                      <option value="administrator">{t('adminCreators.role.administrator')}</option>
                    </select>
                  </td>
                  <td style={{ padding: '0.75rem' }} onClick={(e) => e.stopPropagation()}>
                    <select 
                      value={creator.status} 
                      onChange={(e) => handleStatusChange(creator.id, e.target.value)}
                      style={{ 
                        background: creator.status === 'approved' ? 'rgba(46, 213, 115, 0.1)' : 
                                  creator.status === 'rejected' ? 'rgba(255, 71, 87, 0.1)' : 
                                  'rgba(255, 165, 2, 0.1)', 
                        border: `1px solid ${
                          creator.status === 'approved' ? 'rgba(46, 213, 115, 0.3)' : 
                          creator.status === 'rejected' ? 'rgba(255, 71, 87, 0.3)' : 
                          'rgba(255, 165, 2, 0.3)'
                        }`, 
                        color: creator.status === 'approved' ? '#2ed573' : 
                               creator.status === 'rejected' ? '#ff4757' : 
                               '#ffa502', 
                        padding: '0.5rem', 
                        borderRadius: '6px',
                        outline: 'none',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      <option value="pending">{t('adminCreators.status.pending')}</option>
                      <option value="approved">{t('adminCreators.status.approved')}</option>
                      <option value="rejected">{t('adminCreators.status.rejected')}</option>
                    </select>
                  </td>
                  <td style={{ padding: '0.75rem' }} onClick={(e) => e.stopPropagation()}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem', color: 'rgba(255,255,255,0.9)', cursor: 'pointer', fontSize: '0.9rem' }}>
                      <input
                        type="checkbox"
                        checked={creator.isTrusted === true || creator.isTrusted === 1}
                        onChange={(e) => handleTrustChange(creator.id, e.target.checked)}
                      />
                      <span>
                        {creator.isTrusted === true || creator.isTrusted === 1
                          ? t('adminCreators.trusted')
                          : t('adminCreators.standard')}
                      </span>
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {creators.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255,255,255,0.5)' }}>
              {t('adminCreators.empty')}
            </div>
          )}

          {totalItems > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              marginTop: '0.75rem',
              padding: '0.5rem 0.25rem',
              flexWrap: 'wrap'
            }}>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                {t('adminCreators.pagination.total')}: <strong>{totalItems}</strong> | {t('adminCreators.pagination.page')} <strong>{page}</strong> {t('adminCreators.pagination.of')} <strong>{totalPages}</strong>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>{t('adminCreators.pagination.rowsPerPage')}:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  style={{
                    background: 'rgba(0,0,0,0.3)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'white',
                    padding: '0.35rem 0.5rem',
                    borderRadius: '6px',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>

                <Button variant="ghost" onClick={() => setPage(prev => Math.max(prev - 1, 1))} disabled={page <= 1}>
                  {t('adminCreators.pagination.previous')}
                </Button>
                <Button variant="ghost" onClick={() => setPage(prev => Math.min(prev + 1, totalPages))} disabled={page >= totalPages}>
                  {t('adminCreators.pagination.next')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminCreatorsPage;